import React, { ChangeEvent, forwardRef } from 'react';
import { Grid, GridProps, TextField, TextFieldProps } from '@material-ui/core';
import { useTranslation } from '@fluentsms/i18n';

export type EmailType = {
  [key: string]: string | boolean | undefined;
  emailAddress?: string | undefined;
  isPrimary?: boolean;
  id?: string; // Note: ID is only used for frontend development
};

export type EmailFieldGroupProps = {
  ContainerComponentProps?: GridProps;
  EmailFieldProps?: TextFieldProps;
  value?: EmailType;
  onChange?: (value: EmailType) => void;
  onBlur?: () => void;
  error?: string | undefined;
};

export const mapToEmailAddresses = (emailAddresses: EmailType[]) => {
  const emailAddressesReq = [];

  for (const email of emailAddresses) {
    const emailRequest = {
      emailAddress: email.emailAddress,
      isPrimary: email.isPrimary,
    };
    emailAddressesReq.push(emailRequest);
  }

  return emailAddressesReq;
};

export const EmailFieldGroup = forwardRef<HTMLDivElement, EmailFieldGroupProps>(
  (
    {
      ContainerComponentProps,
      EmailFieldProps,
      value: valueProp = { emailAddress: '' },
      error,
      onChange = () => undefined,
      onBlur = () => undefined,
    }: EmailFieldGroupProps,
    ref,
  ): JSX.Element => {
    const { t } = useTranslation('common');

    const handleChange = (event: string | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (typeof event === 'string') {
        onChange({ ...valueProp, emailAddress: event });
      } else {
        onChange({ ...valueProp, emailAddress: event?.currentTarget?.value });
      }
    };

    return (
      <Grid ref={ref} container {...ContainerComponentProps}>
        <Grid item xs>
          <TextField
            label={t('EmailFieldGroup.emailLabel')}
            inputProps={{ 'data-testid': 'email-field-input' }}
            data-testid="email-field-textfield"
            fullWidth
            onChange={handleChange}
            onBlur={onBlur}
            name="emailAddress"
            value={valueProp?.emailAddress}
            error={!!error}
            helperText={error}
            placeholder={t('EmailFieldGroup.emailPlaceHolder')}
            {...EmailFieldProps}
          />
        </Grid>
      </Grid>
    );
  },
);

EmailFieldGroup.displayName = 'EmailFieldGroup';

export default EmailFieldGroup;
